import { PLANS, type Plan } from "@catmail/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Inbox, Pencil, UserPlus } from "lucide-react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";

import { Button, EmptyState, IconButton, Snackbar } from "@/components";
import { adminApi, authApi } from "@/features/mail/api/client";
import { EditAddressDialog } from "@/features/mail/components";

/**
 * 최고 관리자가 받아주는 화면.
 *
 * 결제가 없는 동안 **사람이 문지기**다. 가입 신청과 주소 신청이 여기로 모인다.
 *
 * 가입 승인에는 집사 등급이 딸려 있다. 등급을 올리는 통로가 지금은 이것뿐이라
 * 여기 없으면 아무도 하우스 위로 못 간다. 주소 승인에는 없다 — **상한을 넘겨
 * 받아주는 것 자체가 관리자의 판단**이고, 사람이 본 것이 숫자보다 위다.
 */

const PLAN_LABEL: Record<Plan, string> = {
  none: "집사 없음",
  house: "하우스",
  mansion: "맨션",
};

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

export function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ["me"], queryFn: authApi.me, retry: false });
  const pendingQuery = useQuery({
    queryKey: ["admin", "pending"],
    queryFn: adminApi.pending,
    enabled: meQuery.data?.isAdmin === true,
  });
  /** 가입 승인할 때 같이 정할 등급. 사람마다 다르므로 id별로 든다. */
  const [plans, setPlans] = useState<Record<string, Plan>>({});

  /** 지금 고치는 중인 주소. 열려 있는 창이 무엇인지도 이 값이 정한다. */
  const [editing, setEditing] = useState<{
    identityId: string;
    address: string;
    approved: boolean;
    isPrimary: boolean;
  } | null>(null);

  /**
   * 지금 누른 줄. 예전에는 이것이 없어서 **모든 줄의 단추가 한꺼번에 죽었고**,
   * 성공해도 알려주는 것이 없었다 — 눌렀는데 아무 일도 안 일어난 것처럼 보였다.
   */
  const [acting, setActing] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: (input: { key: string; done: string; run: () => Promise<unknown> }) =>
      input.run(),
    onMutate: (input) => setActing(input.key),
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "pending"] });
      setNotice(input.done);
    },
    // 서버가 이유를 말해주면 그걸 그대로 보여준다. "처리하지 못했어요"만으로는
    // 이미 쓰이는 주소인지 잠깐 끊긴 것인지 알 수 없다.
    onError: (error) =>
      setNotice(error instanceof Error ? error.message : "처리하지 못했어요"),
    onSettled: () => setActing(null),
  });

  const editMutation = useMutation({
    mutationFn: (input: { identityId: string; localPart: string }) =>
      adminApi.changeAddress(input.identityId, input.localPart),
    onSuccess: async (result) => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["admin", "pending"] });
      // 내 주소를 고쳤을 수도 있다. 프로필에 옛 주소가 남아 있으면 안 된다.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      setNotice(`${result.previous} 을 ${result.address} 으로 고쳤어요`);
    },
  });

  if (meQuery.isError) return <Navigate to="/login" replace />;
  // 관리자가 아니면 이런 화면이 있다는 것도 알리지 않는다.
  if (meQuery.isSuccess && !meQuery.data.isAdmin) return <Navigate to="/" replace />;

  const users = pendingQuery.data?.users ?? [];
  const identities = pendingQuery.data?.identities ?? [];
  const empty = users.length === 0 && identities.length === 0;

  return (
    <div className="bg-paper h-full overflow-y-auto">
      <header className="border-line bg-paper sticky top-0 z-10 flex h-16 items-center gap-2 border-b px-4 sm:px-6">
        <IconButton label="돌아가기" onClick={() => void navigate("/")}>
          <ArrowLeft />
        </IconButton>
        <h1 className="text-ink text-[15px] font-semibold">받아주기</h1>
      </header>

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-4 py-8 sm:px-6">
        {pendingQuery.isPending ? null : empty ? (
          <EmptyState
            icon={<Inbox />}
            title="기다리는 신청이 없어요"
            description="새로 가입하거나 주소를 신청하면 여기에 모여요."
          />
        ) : null}

        {/* --- 가입 신청 ------------------------------------------------- */}
        {users.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-ink flex items-center gap-2 text-[15px] font-semibold">
              <UserPlus className="text-muted-2 size-4" />
              가입 {users.length}
            </h2>

            {users.map((user) => {
              const plan = plans[user.id] ?? "none";

              return (
                <div
                  key={user.id}
                  className="border-line bg-surface flex flex-col gap-3 rounded-[16px] border p-4 sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="text-ink text-sm font-semibold">
                      {user.displayName}
                    </p>
                    <div className="flex min-w-0 items-center gap-1">
                      <p className="text-muted-2 truncate text-[13px]">
                        {user.address}
                      </p>
                      {/*
                        오타 하나 때문에 거절하면 신청자는 이유를 모른 채 같은
                        주소로 다시 신청한다. 고쳐서 받아주는 길을 여기 둔다.
                      */}
                      {user.addressId ? (
                        <IconButton
                          size="sm"
                          label={`${user.address} 고치기`}
                          onClick={() =>
                            setEditing({
                              identityId: user.addressId as string,
                              address: user.address,
                              // 가입 대기 중이면 그 주소도 아직 안 열렸다.
                              approved: false,
                              isPrimary: true,
                            })
                          }
                        >
                          <Pencil />
                        </IconButton>
                      ) : null}
                    </div>
                    <p className="text-muted-3 text-[12px]">
                      {formatDate(user.createdAt)} 신청
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {/*
                      등급을 여기서 정한다. 결제가 없는 동안 올리는 길이 이것뿐이라,
                      승인만 하고 등급을 못 주면 전원이 주소 1개에 묶인다.
                    */}
                    <label className="sr-only" htmlFor={`plan-${user.id}`}>
                      집사 등급
                    </label>
                    <select
                      id={`plan-${user.id}`}
                      value={plan}
                      onChange={(event) =>
                        setPlans((current) => ({
                          ...current,
                          [user.id]: event.target.value as Plan,
                        }))
                      }
                      className="border-line-input bg-surface text-body focus:outline-ink rounded-lg border px-2.5 py-2 text-[13px] outline-none focus:outline-2 focus:outline-offset-2"
                    >
                      {PLANS.map((value) => (
                        <option key={value} value={value}>
                          {PLAN_LABEL[value]}
                        </option>
                      ))}
                    </select>

                    <Button
                      size="sm"
                      disabled={act.isPending}
                      onClick={() =>
                        act.mutate({
                          key: `approve-user-${user.id}`,
                          done: `${user.displayName} 님을 받았어요`,
                          run: () => adminApi.approveUser(user.id, plan),
                        })
                      }
                    >
                      {acting === `approve-user-${user.id}` ? "받는 중…" : "받기"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={act.isPending}
                      onClick={() =>
                        act.mutate({
                          key: `reject-user-${user.id}`,
                          done: `${user.displayName} 님의 신청을 거절했어요`,
                          run: () => adminApi.rejectUser(user.id),
                        })
                      }
                    >
                      거절
                    </Button>
                  </div>
                </div>
              );
            })}
          </section>
        ) : null}

        {/* --- 주소 신청 ------------------------------------------------- */}
        {identities.length > 0 ? (
          <section className="flex flex-col gap-3">
            <h2 className="text-ink flex items-center gap-2 text-[15px] font-semibold">
              <Inbox className="text-muted-2 size-4" />
              주소 {identities.length}
            </h2>

            {identities.map((identity) => (
              <div
                key={identity.id}
                className="border-line bg-surface flex flex-col gap-3 rounded-[16px] border p-4 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex min-w-0 items-center gap-1">
                    <p className="text-ink truncate text-sm font-semibold">
                      {identity.address}
                    </p>
                    <IconButton
                      size="sm"
                      label={`${identity.address} 고치기`}
                      onClick={() =>
                        setEditing({
                          identityId: identity.id,
                          address: identity.address,
                          // 여기 뜬 것은 전부 아직 안 받아준 주소다.
                          approved: false,
                          isPrimary: false,
                        })
                      }
                    >
                      <Pencil />
                    </IconButton>
                  </div>
                  <p className="text-muted-2 text-[13px]">
                    {identity.owner.displayName} · {PLAN_LABEL[identity.owner.plan]}
                  </p>
                  {/*
                    상한 대신 **지금 몇 개를 쓰는지**를 적는다. 상한은 아무것도
                    막지 않으므로 그것을 적으면 "넘었으니 거절"이라는 없는 규칙을
                    만들어낸다. 판단에 필요한 것은 규칙이 아니라 사실이다.
                  */}
                  <p className="text-muted-3 text-[12px]">
                    {formatDate(identity.createdAt)} 신청 · 지금 주소 {identity.owned}개
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    size="sm"
                    disabled={act.isPending}
                    onClick={() =>
                      act.mutate({
                        key: `approve-identity-${identity.id}`,
                        done: `${identity.address} 을 열었어요`,
                        run: () => adminApi.approveIdentity(identity.id),
                      })
                    }
                  >
                    {acting === `approve-identity-${identity.id}` ? "받는 중…" : "받기"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={act.isPending}
                    onClick={() =>
                      act.mutate({
                        key: `reject-identity-${identity.id}`,
                        done: `${identity.address} 신청을 거절했어요`,
                        run: () => adminApi.rejectIdentity(identity.id),
                      })
                    }
                  >
                    거절
                  </Button>
                </div>
              </div>
            ))}
          </section>
        ) : null}
      </div>

      <EditAddressDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        address={editing?.address ?? ""}
        // 도메인은 하나뿐이라 고치는 주소에서 떼어 쓴다. 따로 물어보면 요청만
        // 늘고, 답이 오기 전까지 `@…`가 잠깐 비어 보인다.
        mailDomain={editing?.address.split("@")[1] ?? ""}
        approved={editing?.approved ?? false}
        isPrimary={editing?.isPrimary ?? false}
        pending={editMutation.isPending}
        error={
          editMutation.error instanceof Error ? editMutation.error.message : null
        }
        onSubmit={(localPart) => {
          if (!editing) return;
          editMutation.mutate({ identityId: editing.identityId, localPart });
        }}
      />

      {notice ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 flex justify-center px-4">
          <Snackbar
            className="pointer-events-auto"
            message={notice}
            actionLabel="닫기"
            onAction={() => setNotice(null)}
          />
        </div>
      ) : null}
    </div>
  );
}
